import React, { useEffect } from 'react';
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useStore, KEYS } from "../admin/store";


const About = () => {
    const team = useStore(KEYS.team);
    const info = useStore(KEYS.settings);
    useEffect(() => {
        document.title = "About MM AutoTech | Trusted Garage Support & Lead Generation UK";
        const desc = 'Discover how MM AutoTech helps UK garages grow through expert lead generation, call handling, admin support, and digital transformation partnerships.';
        const keywords = 'MM AutoTech, garage support, strategies for business growth, digital marketing for garages, garage marketing, how to increase sales in a garage business, how to get cheap parts for garage, how to improve customer service, customer service provider, garage customer service, garage';
        const updateMeta = (name, content) => {
            let tag = document.querySelector(`meta[name="${name}"]`);
            if (tag) {
                tag.setAttribute('content', content);
            } else {
                tag = document.createElement('meta');
                tag.name = name;
                tag.content = content;
                document.head.appendChild(tag);
            }
        };

        updateMeta('description', desc);
        updateMeta('keywords', keywords);
    }, []);
    return (


        <><Helmet>
            <title>About MM AutoTech | Garage Growth Experts</title>
            <meta
                name="description"
                content="Learn about MM AutoTech, your trusted partner for lead generation, parts sourcing, and digital marketing for UK garages."
            />
            <link rel="canonical" href="https://mmautotech.com/about" />
        </Helmet>


            <div className="bg-gray-50">
                {/* Hero Section */}
                <section className="relative min-h-[100dvh]">
                    <img
                        src="/assets/about/1.jpg"
                        alt="Independent garage workshop with vehicles on lifts"
                        className="absolute inset-0 w-full h-full object-cover"
                        width="1920"
                        height="1080"
                        loading="eager"
                        decoding="async" />
                    <div className="absolute inset-0 bg-black/70" />
                    <div className="relative z-10 flex items-center justify-end h-full px-6 sm:px-10 max-w-7xl mx-auto">
                        <div className="text-white max-w-xl text-right">
                            <span className="bg-blue-800 text-blue-200 font-semibold uppercase tracking-widest px-4 py-1 inline-block rounded mb-4">
                                Trusted UK Garage Growth Partner
                            </span>
                            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4">
                                Powering Local Garages with Growth, Leads & Support
                            </h1>
                            <p className="text-lg text-gray-300 mb-8">
                                MM AutoTech provides end-to-end solutions for independent garages across the UK — from
                                <strong> lead generation</strong> and <strong>digital marketing</strong> to <strong>parts sourcing</strong> and <strong>admin support</strong>.
                            </p>
                            <div className="flex flex-col sm:flex-row justify-end gap-4">
                                <Link to="/services">
                                    <button className="px-6 py-3 border border-white text-white font-semibold hover:bg-white hover:text-blue-900 transition rounded">
                                        View All Services
                                    </button>
                                </Link>
                                <Link to="/contact">
                                    <button className="px-6 py-3 bg-blue-700 hover:bg-blue-600 text-white font-semibold transition rounded">
                                        Book Free Consultation
                                    </button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* About Section */}
                <section className="py-20 bg-gray-50 px-4 sm:px-6">
                    <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">
                        {/* Image Left (mobile: bottom) */}
                        <div className="flex justify-center order-2 md:order-1">
                            <img
                                src="/assets/about/2.jpg"

                                alt="Digital tools for garages"
                                className="rounded-xl shadow-lg w-full max-w-md object-cover" />
                        </div>

                        {/* Text Content */}
                        <div className="order-1 md:order-2">
                            <h2 className="text-4xl font-extrabold text-blue-900 mb-6">
                                How We Power Garage Success
                            </h2>
                            <p className="text-gray-700 text-lg mb-5">
                                Most independent garages struggle with low online visibility, missed calls, and admin overload.
                                <strong> MM AutoTech</strong> fixes that with done-for-you growth systems built for UK garages.
                            </p>
                            <p className="text-gray-700 text-lg mb-5">
                                We act as your virtual back office — attracting customers, answering every enquiry, and
                                tightening operations — so you can focus on repairs, not paperwork.
                            </p>
                            <ul className="space-y-4 text-gray-700 text-base mt-6">
                                <li>✓ <Link to="/services#local-seo" className="underline">Google Maps & SEO</Link> that drive high-intent enquiries</li>
                                <li>✓ <Link to="/services#admin" className="underline">Virtual receptionists</Link> for calls, bookings & follow-ups</li>
                                <li>✓ <Link to="/services#ads" className="underline">Ad funnels</Link> with transparent ROI</li>
                                <li>✓ <Link to="/services#parts" className="underline">Parts sourcing</Link> from trusted suppliers</li>
                                <li>✓ Customised, white-label support that fits your workflow</li>
                            </ul>
                        </div>
                    </div>
                </section>
                {/* What We Do Section */}
                <section className="py-20 bg-gray-50 px-4 sm:px-6">
                    <div className="max-w-7xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-blue-900 mb-8 text-center md:text-left">
                            What MM Autotech Do
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                            {/* Left Side: Text */}
                            <div className="order-2 md:order-1">
                                <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-5">
                                    MM Autotech empowers UK-based automotive workshops and garages to accelerate their growth
                                    by providing comprehensive, industry-specific business solutions. From strategic business
                                    support and expert lead generation to exceptional customer service and targeted digital
                                    marketing campaigns, we deliver everything your workshop needs to stand out in a competitive market.
                                </p>
                                <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-5">
                                    Our tailored services are designed to increase your sales pipeline, improve customer
                                    retention, and optimize your operational efficiency. Whether you want to attract more
                                    local customers, boost online visibility, or streamline your logistics, MM Autotech offers
                                    a full suite of solutions crafted specifically for the auto industry.
                                </p>
                                <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                                    With our proven strategies and data-driven approach, we help garages increase qualified
                                    leads and convert them into loyal customers. We understand the unique challenges faced by
                                    automotive workshops and offer expert guidance to help you maximize revenue while minimizing downtime.
                                </p>
                            </div>

                            {/* Right Side: Image */}
                            <div className="flex justify-center order-1 md:order-2">
                                <img
                                    src="/assets/about/3.jpg"

                                    alt="Office Team Collaboration"
                                    className="rounded-lg shadow-md w-full max-w-lg h-auto object-cover" />
                            </div>
                        </div>
                    </div>
                </section>


                <section
                    id="values"
                    className="py-24 bg-white px-4 sm:px-6"
                    aria-labelledby="values-title"
                >
                    <div className="max-w-6xl mx-auto text-center">
                        <h2
                            id="values-title"
                            className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-blue-900 mb-6"
                        >
                            Our Core Values
                        </h2>

                        <p className="text-gray-700 text-lg sm:text-xl mb-14 max-w-4xl mx-auto">
                            At <strong>MM AutoTech</strong>, we’re a growth partner for UK garages — not just a vendor.
                            These principles guide how we deliver <em>garage support</em>, <em>digital marketing for garages</em>,
                            and <em>customer service</em> that measurably grows your business.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8 text-left">
                            {/* Value 1 */}
                            <article className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition">
                                <header className="flex items-start gap-3 mb-3">
                                    <span className="inline-flex w-9 h-9 rounded-lg bg-blue-100 text-blue-800 items-center justify-center font-bold" aria-hidden="true">1</span>
                                    <h3 className="text-2xl font-bold text-blue-800">Garage-Centric Focus</h3>
                                </header>
                                <p className="text-gray-600 text-lg">
                                    We work exclusively with independent garages, so our playbooks match your day-to-day —
                                    consumer expectations, seasonality, and local search behaviour.
                                </p>
                                <ul className="mt-4 text-gray-600 list-disc pl-5 text-sm leading-relaxed">
                                    <li>Local SEO aligned to real search intents (MOT, diagnostics, tyres, servicing)</li>
                                    <li>Operational fit for small teams and multi-bay workshops</li>
                                </ul>
                            </article>

                            {/* Value 2 */}
                            <article className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition">
                                <header className="flex items-start gap-3 mb-3">
                                    <span className="inline-flex w-9 h-9 rounded-lg bg-blue-100 text-blue-800 items-center justify-center font-bold" aria-hidden="true">2</span>
                                    <h3 className="text-2xl font-bold text-blue-800">
                                        Partnership Over Profit
                                    </h3>
                                </header>
                                <p className="text-gray-600 text-lg">
                                    No lock-ins. No aggressive upsells. We focus on ROI — and we earn renewals through results.
                                </p>
                                <ul className="mt-4 text-gray-600 list-disc pl-5 text-sm leading-relaxed">
                                    <li>Transparent goals, reports, and monthly reviews</li>
                                    <li>Flexible scope as your garage grows</li>
                                </ul>
                            </article>

                            {/* Value 3 */}
                            <article className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition">
                                <header className="flex items-start gap-3 mb-3">
                                    <span className="inline-flex w-9 h-9 rounded-lg bg-blue-100 text-blue-800 items-center justify-center font-bold" aria-hidden="true">3</span>
                                    <h3 className="text-2xl font-bold text-blue-800">Innovation with Simplicity</h3>
                                </header>
                                <p className="text-gray-600 text-lg">
                                    Modern funnels and tools — without complexity. We implement what works, then train your team.
                                </p>
                                <ul className="mt-4 text-gray-600 list-disc pl-5 text-sm leading-relaxed">
                                    <li>GBP optimisation, review systems, and booking automations</li>
                                    <li>Playbooks you can run even if we step back</li>
                                </ul>
                            </article>

                            {/* Value 4 */}
                            <article className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition">
                                <header className="flex items-start gap-3 mb-3">
                                    <span className="inline-flex w-9 h-9 rounded-lg bg-blue-100 text-blue-800 items-center justify-center font-bold" aria-hidden="true">4</span>
                                    <h3 className="text-2xl font-bold text-blue-800">Customer-First Support</h3>
                                </header>
                                <p className="text-gray-600 text-lg">
                                    Your callers and walk-ins are treated like our own. We aim for faster replies, clearer quotes,
                                    and happier repeat customers.
                                </p>
                                <ul className="mt-4 text-gray-600 list-disc pl-5 text-sm leading-relaxed">
                                    <li>Call handling, reminders, and follow-ups that convert</li>
                                    <li>Consistent tone and white-label workflows</li>
                                </ul>
                            </article>
                        </div>
                    </div>
                </section>

                {/* Mission & Leadership */}
                <section id="mission" className="py-20 bg-gray-100 px-4 sm:px-6" aria-labelledby="mission-title">
                    <div className="max-w-7xl mx-auto">
                        {/* Intro */}
                        <div className="text-center max-w-3xl mx-auto">
                            <p className="text-gray-700 text-lg mb-10">
                                Our leadership brings deep experience across automotive operations, digital transformation, and customer service—
                                all focused on helping UK garages grow smarter and faster.
                            </p>
                        </div>

                        {/* Mission */}
                        <div className="text-center max-w-4xl mx-auto">
                            <h2 id="mission-title" className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
                                Our Mission
                            </h2>
                            <p className="text-gray-700 text-lg mb-4">
                                Help independent UK garages thrive with streamlined systems and practical, white-label support.
                            </p>
                            <p className="text-gray-700 text-lg mb-4">
                                Whether it’s attracting customers, improving operations, or expanding reach, <strong>MM AutoTech</strong> brings the
                                right tools, people, and partnerships to deliver lasting results.
                            </p>
                            <p className="text-gray-700 text-lg">
                                We’re committed to transparency, quality, and innovation for every partner garage we serve.
                            </p>

                            {/* Quick proof / stats (adjust numbers to your real figures) */}
                            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="bg-white rounded-xl shadow p-5">
                                    <div className="text-3xl font-extrabold text-blue-900">90 days</div>
                                    <div className="text-sm text-gray-500">Typical time to visible SEO lift</div>
                                </div>
                                <div className="bg-white rounded-xl shadow p-5">
                                    <div className="text-3xl font-extrabold text-blue-900">+30–80</div>
                                    <div className="text-sm text-gray-500">Extra monthly calls with call handling</div>
                                </div>
                                <div className="bg-white rounded-xl shadow p-5">
                                    <div className="text-3xl font-extrabold text-blue-900">24/7</div>
                                    <div className="text-sm text-gray-500">Lead capture & booking funnels</div>
                                </div>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="h-px bg-gray-200 my-12" />

                        {/* Leadership */}
                        <section
                            id="leadership"
                            aria-labelledby="leadership-title"
                            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
                        >
                            <h2
                                id="leadership-title"
                                className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-900 text-center mb-10"
                            >
                                Leadership
                            </h2>

                            <div className="grid gap-10 md:grid-cols-2 items-start">
                                {/* Leader Card */}
                                <article
                                    className="bg-white p-6 rounded-xl shadow hover:shadow-md transition"
                                    aria-label="Founder profile"
                                >
                                    <figure className="flex flex-col sm:flex-row items-center sm:items-start gap-5 text-center sm:text-left">
                                        <img
                                            src="/assets/ceo_mm_autotech.svg"
                                            alt="Portrait of Saleem Noor, Founder & CEO of MM AutoTech"
                                            className="w-32 h-32 sm:w-40 sm:h-40 rounded-full object-cover flex-shrink-0"
                                            width="130"
                                            height="130"
                                            loading="lazy"
                                            decoding="async" />
                                        <figcaption>
                                            <h3 className="text-lg sm:text-xl font-semibold text-blue-900">
                                                Saleem Noor
                                            </h3>
                                            <p className="text-sm text-gray-600">Founder &amp; CEO</p>
                                            <p className="text-gray-600 text-sm sm:text-base mt-3 leading-relaxed">
                                                With over a decade of expertise in automotive operations and
                                                customer experience, we specialize in designing practical and
                                                measurable growth systems that enhance efficiency, strengthen
                                                performance, and support the long-term success of independent
                                                garages.
                                            </p>
                                        </figcaption>
                                    </figure>
                                </article>

                                {/* Founder Note */}
                                <article className="bg-white p-6 rounded-xl shadow hover:shadow-md transition text-center sm:text-left">
                                    <h3 className="text-lg sm:text-xl font-semibold text-blue-900 mb-2">
                                        A note from our founder
                                    </h3>
                                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                                        We built <strong>MM AutoTech</strong> to give independent garages the
                                        same growth engines big chains rely on—without the heavy overhead.
                                        With over a decade in automotive operations, I’ve seen how the right
                                        systems can transform calls, reviews, and daily efficiency. Our
                                        mission is simple: to help garages grow sustainably, so you can focus
                                        on what you do best—serving your customers.
                                    </p>
                                    <div className="mt-4 flex flex-col sm:flex-row justify-center sm:justify-start gap-3">
                                        <Link
                                            to="/contact"
                                            className="px-5 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-700 transition"
                                        >
                                            Book a free consultation
                                        </Link>
                                        <Link
                                            to="/services"
                                            className="px-5 py-2 border rounded-lg hover:bg-gray-50 transition"
                                        >
                                            Explore our services
                                        </Link>
                                    </div>
                                </article>
                            </div>




                            {/* ================= TEAM SECTION ================= */}
                            <section id="team" className="py-20 bg-gray-50 px-4 sm:px-6">
                                <h2 className="text-3xl sm:text-4xl font-bold text-blue-900 text-center mb-12">
                                    Meet Our Team
                                </h2>

                                <div className="grid gap-10 sm:grid-cols-2 md:grid-cols-3">
                                    {team.map((member) => (
                                        <div key={member.id} className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition text-center flex flex-col h-full">
                                            <img
                                                src={member.photo}
                                                alt={`${member.name}, ${member.role}`}
                                                className="w-60 h-60 mx-auto mb-4 object-cover rounded-lg"
                                                loading="lazy"
                                            />
                                            <h3 className="text-lg font-semibold text-blue-900">{member.name}</h3>
                                            <p className="text-sm text-gray-600">{member.role}</p>
                                            <p className="text-sm text-gray-600">{member.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>



                            {/* Secondary CTA row */}
                            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
                                <Link
                                    to="/contact"
                                    className="px-5 py-2 bg-blue-900 text-white rounded hover:bg-blue-700"
                                >
                                    Get a growth plan
                                </Link>
                                <Link
                                    to="/contact"
                                    className="px-5 py-2 border rounded hover:bg-gray-50"
                                >
                                    Talk to our team
                                </Link>
                            </div>
                        </section>
                    </div>
                </section>

                {/* PARTNERSHIPS / ASSOCIATIONS */}
                <section
                    id="partnerships"
                    className="py-20 bg-white px-4 sm:px-6"
                    aria-labelledby="partnerships-title"
                >
                    <div className="max-w-7xl mx-auto text-center">
                        <h2 id="partnerships-title" className="text-3xl sm:text-4xl font-bold text-blue-900 mb-6">
                            Strategic Partnerships &amp; Associations
                        </h2>
                        <p className="text-gray-700 max-w-2xl mx-auto mb-12 text-lg">
                            MM AutoTech collaborates with reputable UK service providers to deliver innovative, reliable,
                            and scalable support for independent garages.
                        </p>

                        <div className="grid md:grid-cols-2 gap-10 items-start justify-center">
                            {/* Certificate 1 */}
                            <figure className="rounded-lg overflow-hidden shadow-lg border border-gray-200 hover:shadow-2xl transition duration-300 bg-white">
                                <a
                                    href="/certificates/Certificate_Association_MMAUTO_PMS.jpg"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Open full-size certificate: MM AutoTech and PMS association"
                                    className="block"
                                >
                                    <img
                                        src="/certificates/Certificate_Association_MMAUTO_PMS.jpg"
                                        alt="Certificate of association between MM AutoTech and PMS"
                                        className="w-full h-auto object-contain"
                                        width="1200"
                                        height="848"
                                        loading="lazy"
                                        decoding="async" />
                                </a>
                                <figcaption className="px-4 py-3 text-sm text-gray-600 border-t">
                                    MM AutoTech × PMS — Strategic association (2024)
                                </figcaption>
                            </figure>

                            {/* Certificate 2 */}
                            <figure className="rounded-lg overflow-hidden shadow-lg border border-gray-200 hover:shadow-2xl transition duration-300 bg-white">
                                <a
                                    href="/certificates/Certificate_Association_MMAUTO_PMS1.png"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Open full-size certificate: MM AutoTech and PMS association (alt version)"
                                    className="block"
                                >
                                    <img
                                        src="/certificates/Certificate_Association_MMAUTO_PMS1.png"
                                        alt="Certificate confirming MM AutoTech association with PMS"
                                        className="w-full h-auto object-contain"
                                        width="1200"
                                        height="848"
                                        loading="lazy"
                                        decoding="async" />
                                </a>
                                <figcaption className="px-4 py-3 text-sm text-gray-600 border-t">
                                    MM AutoTech × PMS — Strategic association (document variant)
                                </figcaption>
                            </figure>
                        </div>

                        {/* Legal clarity (prevents implied endorsement) */}
                        <p className="text-xs text-gray-500 mt-6 italic">
                            Certificates and logos are property of their respective owners. Partnerships indicate
                            collaboration and service delivery; they do not imply endorsement unless explicitly stated.
                        </p>
                    </div>
                </section>

                {/* TESTIMONIALS */}
                <section
                    id="testimonials"
                    className="py-20 bg-gray-50 px-4 sm:px-6"
                    aria-labelledby="testimonials-title"
                >
                    <div className="max-w-6xl mx-auto text-center">
                        <h2 id="testimonials-title" className="text-3xl sm:text-4xl font-bold text-blue-900 mb-4">
                            What Partner Garages Say
                        </h2>
                        <p className="text-gray-700 text-lg max-w-2xl mx-auto mb-12">
                            Garages across the UK trust MM AutoTech for measurable growth and dependable support.
                        </p>

                        <div className="grid md:grid-cols-3 gap-8 text-left">
                            {/* Card 1 */}
                            <article
                                className="bg-white p-6 rounded-xl shadow hover:shadow-md transition"
                                itemScope
                                itemType="https://schema.org/Review"
                                aria-label="Testimonial from West London Motors"
                            >
                                <div className="flex items-center gap-2 mb-3" aria-hidden="true">
                                    {[...Array(5)].map((_, i) => (
                                        <svg key={i} className="w-5 h-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.46c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.719c-.784-.57-.38-1.81.588-1.81h3.461a1 1 0 00.95-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>
                                <p className="text-gray-800 mb-4 italic" itemProp="reviewBody">
                                    “MM AutoTech took us from invisible on Maps to phones ringing all day. We added a bay to keep up.”
                                </p>
                                <div className="font-semibold text-blue-900" itemProp="author" itemScope itemType="https://schema.org/Person">
                                    <span itemProp="name">Saleem Sheikh</span>
                                    <meta itemProp="jobTitle" content="Owner" />
                                </div>
                                <div className="text-sm text-gray-500">AM Motors — Local SEO, Reviews, GBP</div>
                                <meta itemProp="itemReviewed" content="MM AutoTech" />
                                <div itemProp="reviewRating" itemScope itemType="https://schema.org/Rating" className="sr-only">
                                    <span itemProp="ratingValue">5</span>/<span itemProp="bestRating">5</span>
                                </div>
                            </article>

                            {/* Card 2 */}
                            <article
                                className="bg-white p-6 rounded-xl shadow hover:shadow-md transition"
                                itemScope
                                itemType="https://schema.org/Review"
                                aria-label="Testimonial from Prime Auto"
                            >
                                <div className="flex items-center gap-2 mb-3" aria-hidden="true">
                                    {[...Array(5)].map((_, i) => (
                                        <svg key={i} className="w-5 h-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.46c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.719c-.784-.57-.38-1.81.588-1.81h3.461a1 1 0 00.95-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>
                                <p className="text-gray-800 mb-4 italic" itemProp="reviewBody">
                                    “Their call handling paid for itself in the first month. We don’t miss enquiries anymore.”
                                </p>
                                <div className="font-semibold text-blue-900" itemProp="author" itemScope itemType="https://schema.org/Person">
                                    <span itemProp="name">Fawad Khan</span>
                                    <meta itemProp="jobTitle" content="Manager" />
                                </div>
                                <div className="text-sm text-gray-500">PMS — Admin &amp; Call Handling</div>
                                <meta itemProp="itemReviewed" content="MM AutoTech" />
                                <div itemProp="reviewRating" itemScope itemType="https://schema.org/Rating" className="sr-only">
                                    <span itemProp="ratingValue">5</span>/<span itemProp="bestRating">5</span>
                                </div>
                            </article>

                            {/* Card 3 */}
                            <article
                                className="bg-white p-6 rounded-xl shadow hover:shadow-md transition"
                                itemScope
                                itemType="https://schema.org/Review"
                                aria-label="Testimonial from City Service Centre"
                            >
                                <div className="flex items-center gap-2 mb-3" aria-hidden="true">
                                    {[...Array(5)].map((_, i) => (
                                        <svg key={i} className="w-5 h-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.46c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.719c-.784-.57-.38-1.81.588-1.81h3.461a1 1 0 00.95-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>
                                <p className="text-gray-800 mb-4 italic" itemProp="reviewBody">
                                    “Ads + landing pages = bookings. Simple, measurable, and finally profitable.”
                                </p>
                                <div className="font-semibold text-blue-900" itemProp="author" itemScope itemType="https://schema.org/Person">
                                    <span itemProp="name">Sohail khaliq yar</span>
                                    <meta itemProp="jobTitle" content="Director" />
                                </div>
                                <div className="text-sm text-gray-500">Perivale Motor Service — Google PPC &amp; Funnels</div>
                                <meta itemProp="itemReviewed" content="MM AutoTech" />
                                <div itemProp="reviewRating" itemScope itemType="https://schema.org/Rating" className="sr-only">
                                    <span itemProp="ratingValue">5</span>/<span itemProp="bestRating">5</span>
                                </div>
                            </article>
                        </div>

                        {/* Secondary CTA */}
                        <div className="mt-10">
                            <a
                                href="https://g.page/r/your-google-profile" // replace with your Google review link if you have one
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-5 py-2 border rounded hover:bg-white"
                            >
                                Read more reviews
                            </a>
                        </div>
                    </div>
                </section>


                {/* Call to Action */}
                <section className="py-16 bg-[#0f172a] text-white px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl mx-auto text-center">
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">Ready to Grow Your Garage?</h2>
                        <p className="text-gray-300 mb-6">
                            Book a free 20-minute consultation. We’ll audit your current setup and recommend a practical growth plan.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Link to="/contact">
                                <button className="inline-flex items-center gap-2 bg-white text-[#0f172a] px-6 py-3 rounded-md font-semibold hover:bg-gray-100">
                                    Book Free Strategy Call
                                </button>
                            </Link>
                            <Link to="/contact">
                                <button className="inline-flex items-center gap-2 border border-white px-6 py-3 rounded-md font-semibold hover:bg-white/10">
                                    Contact Our Team
                                </button>
                            </Link>
                        </div>
                        <p className="text-sm text-gray-400 mt-4">
                            Prefer email? <a href={`mailto:${info.email}`} className="hover:text-white hover:underline">{info.email}</a>
                        </p>
                    </div>
                </section>
            </div></>
    );
};

export default About;
